import React, { useState, useEffect } from 'react';
import { X, Loader2, Check, AlertCircle, FileText, Image } from 'lucide-react';
import WhatsAppIcon from '../icons/WhatsAppIcon';
import { toast } from 'react-toastify';
import {
  validatePhoneNumber,
  normalizePhoneNumber,
  getNetworkProvider
} from '../../api/whatsapp';

// API base URL
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

/**
 * ReceiptWhatsAppModal Component
 * Modal for sending receipt via WhatsApp
 */
const ReceiptWhatsAppModal = ({ order, receipt, onClose, onSuccess, generateImage }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [sending, setSending] = useState(false);
  const [validation, setValidation] = useState({ isValid: false, message: '' });
  const [networkProvider, setNetworkProvider] = useState('');
  const [format, setFormat] = useState('pdf'); // 'pdf' or 'image'

  // Initialize phone number from order distributor
  useEffect(() => {
    if (order && order.distributor_phone) {
      setPhoneNumber(order.distributor_phone);
    }
  }, [order]);

  // Validate phone number on change
  useEffect(() => {
    if (phoneNumber.trim()) {
      const result = validatePhoneNumber(phoneNumber);
      setValidation(result);

      if (result.isValid) {
        const provider = getNetworkProvider(phoneNumber);
        setNetworkProvider(provider);
      } else {
        setNetworkProvider('');
      }
    } else {
      setValidation({ isValid: false, message: '' });
      setNetworkProvider('');
    }
  }, [phoneNumber]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!phoneNumber.trim()) {
      toast.error('Please enter a phone number');
      return;
    }

    // Validate phone number
    const validationResult = validatePhoneNumber(phoneNumber);
    if (!validationResult.isValid) {
      toast.error(validationResult.message);
      return;
    }

    setSending(true);

    try {
      // Normalize phone number to international format
      const normalizedPhone = normalizePhoneNumber(phoneNumber);

      let imageBase64 = null;
      
      // If image format selected and generateImage callback provided, generate the image
      if (format === 'image' && generateImage) {
        try {
          toast.info('Generating image...');
          const imageBlob = await generateImage();
          // Convert blob to base64
          imageBase64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(',')[1]); // Remove data:image/png;base64, prefix
            reader.onerror = reject;
            reader.readAsDataURL(imageBlob);
          });
        } catch (error) {
          console.error('Image generation error:', error);
          toast.error('Failed to generate image');
          setSending(false);
          return;
        }
      }

      // Send receipt via WhatsApp
      const response = await fetch(`${API_URL}/wholesale/orders/${order.id}/receipt/send-whatsapp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify({
          phoneNumber: normalizedPhone,
          format: format,
          imageBase64: imageBase64,
          receiptId: receipt?.id
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send receipt');
      }

      toast.success(`Receipt sent successfully via WhatsApp as ${format.toUpperCase()}`);

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (error) {
      console.error('WhatsApp send error:', error);
      toast.error(error.message || 'Failed to send receipt via WhatsApp');
    } finally {
      setSending(false);
    }
  };

  const handlePhoneChange = (e) => {
    // Allow only digits, +, spaces, and hyphens
    const value = e.target.value.replace(/[^\d+\s-]/g, '');
    setPhoneNumber(value);
  };

  if (!order) return null;

  const receiptNumber = receipt?.receipt_number || order.receipt_number || 'N/A';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[70]">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <WhatsAppIcon className="w-5 h-5 text-green-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Send Receipt via WhatsApp
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-4">
          {/* Receipt Info */}
          <div className="bg-gray-50 p-3 rounded-lg mb-4 border-2 border-emerald-600">
            <p className="text-sm text-gray-600">Receipt Number</p>
            <p className="font-semibold text-gray-900">{receiptNumber}</p>
            <p className="text-sm text-gray-600 mt-2">Order Number</p>
            <p className="font-medium text-gray-900">{order.order_number}</p>
            <p className="text-sm text-gray-600 mt-2">Customer</p>
            <p className="font-medium text-gray-900">{order.distributor_business_name || 'Customer'}</p>
            <p className="text-sm text-gray-600 mt-2">Amount Paid</p>
            <p className="font-semibold text-emerald-600">
              ₦{parseFloat(receipt?.payment_amount || receipt?.amount_paid || order.amount_paid || order.total_amount || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
            </p>
          </div>

          {/* Format Toggle */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Document Format
            </label>
            <div className="flex rounded-lg border border-gray-300 overflow-hidden">
              <button
                type="button"
                onClick={() => setFormat('pdf')}
                disabled={sending}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                  format === 'pdf'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <FileText className="w-4 h-4" />
                <span>PDF Document</span>
              </button>
              <button
                type="button"
                onClick={() => setFormat('image')}
                disabled={sending}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-l border-gray-300 ${
                  format === 'image'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <Image className="w-4 h-4" />
                <span>Image (PNG)</span>
              </button>
            </div>
          </div>

          {/* Phone Number Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-900 mb-2">
              WhatsApp Phone Number <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="tel"
                value={phoneNumber}
                onChange={handlePhoneChange}
                placeholder="+234 801 234 5678 or 08012345678"
                className={`w-full px-4 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                  phoneNumber && validation.isValid
                    ? 'border-green-500 focus:ring-green-500'
                    : phoneNumber && !validation.isValid
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-emerald-600'
                }`}
                required
                disabled={sending}
              />
              {phoneNumber && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {validation.isValid ? (
                    <Check className="w-5 h-5 text-green-500" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  )}
                </div>
              )}
            </div>

            {/* Validation Message */}
            {phoneNumber && validation.message && (
              <p
                className={`text-sm mt-1 ${
                  validation.isValid ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {validation.message}
                {validation.isValid && networkProvider && networkProvider !== 'Unknown' && (
                  <span className="ml-1 text-gray-600">
                    ({networkProvider})
                  </span>
                )}
              </p>
            )}

            {/* Format Hint */}
            <p className="text-xs text-gray-600 mt-1">
              Format: +234 801 234 5678 or 08012345678
            </p>
          </div>

          {/* Info Note */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> The receipt will be sent as {format === 'pdf' ? 'a PDF document' : 'an image (PNG)'} via WhatsApp.
              Make sure the phone number is registered on WhatsApp.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={sending}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={sending || !validation.isValid}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <WhatsAppIcon className="w-4 h-4" />
                  <span>Send via WhatsApp</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReceiptWhatsAppModal;
